import { z } from "zod";
import {
  FiscalDocumentType,
  PaymentMethod,
  SalesDocumentType,
  RecurringFrequency,
  ReconciliationMode,
  SefMode,
  PfrMode,
  SkuFormat,
  EmailTemplateDesign,
  CompanyUserRole,
  PosQuantityMode,
  PERMISSION_KEYS,
} from "@finora/types";

/**
 * @finora/validation
 *
 * Zod šeme koje su jedini izvor istine za oblik i pravila validacije
 * ulaznih podataka. Frontend ih koristi sa React Hook Form
 * (zodResolver), backend ih koristi u DTO klasama (vidi
 * apps/api/src/modules/*\/dto) da se pravila ne bi pisala dvaput.
 */

// Decimalni string sa najviše 4 decimale (dovoljno za količine i cene).
// Ne dozvoljava negativan predznak po difoltu - konkretne šeme ga eksplicitno
// dozvoljavaju samo tamo gde ima smisla (npr. nikad za količinu).
const decimalString = (opts?: { allowZero?: boolean }) =>
  z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, "Iznos mora biti pozitivan broj (npr. 12.50)")
    .refine(
      (v) => (opts?.allowZero ? Number(v) >= 0 : Number(v) > 0),
      opts?.allowZero
        ? "Iznos ne sme biti negativan"
        : "Iznos mora biti veći od nule",
    );

export const quantitySchema = decimalString().refine(
  (v) => Number(v) > 0,
  "Količina mora biti veća od nule",
);

export const moneyAmountSchema = decimalString();
export const nonNegativeMoneyAmountSchema = decimalString({ allowZero: true });

export const createFiscalDocumentSchema = z.object({
  businessLocationId: z.string().uuid(),
  type: z.nativeEnum(FiscalDocumentType),
  customerId: z.string().uuid().nullable().optional(),
  currency: z.string().length(3).default("RSD"),
  note: z.string().max(1000).nullable().optional(),
});
export type CreateFiscalDocumentInput = z.infer<
  typeof createFiscalDocumentSchema
>;

// Avans-Prodaja (razgovor 23.09.2026) - poseban tok, NE ide preko
// createFiscalDocumentSchema + addFiscalDocumentItemSchema, jer avansni
// račun NIKAD ne sme sadržati stvarne artikle budućeg prometa, samo jednu
// generičku "Аванс" stavku u iznosu stvarno primljenog avansa. `amount` i
// stvarno registrovano plaćanje moraju biti isti iznos (servis to
// garantuje - ovde samo validiramo oblik unosa).
export const createAdvanceSaleSchema = z.object({
  businessLocationId: z.string().uuid(),
  customerId: z.string().uuid().nullable().optional(),
  buyerPib: z.string().nullable().optional(),
  buyerName: z.string().nullable().optional(),
  buyerIdType: z.string().nullable().optional(),
  buyerAddress: z.string().nullable().optional(),
  taxRateId: z.string().uuid(),
  amount: moneyAmountSchema,
  paymentMethod: z.nativeEnum(PaymentMethod),
  receivedAmount: moneyAmountSchema.optional(),
  // Kombinovano plaćanje avansa: više načina, zbir mora biti jednak iznosu.
  payments: z
    .array(z.object({ method: z.nativeEnum(PaymentMethod), amount: moneyAmountSchema }))
    .max(6)
    .optional(),
  note: z.string().max(1000).nullable().optional(),
});
export type CreateAdvanceSaleInput = z.infer<typeof createAdvanceSaleSchema>;

export const addFiscalDocumentItemSchema = z.object({
  productId: z.string().uuid().nullable().optional(),
  name: z.string().min(1, "Naziv stavke je obavezan").max(255),
  quantity: quantitySchema,
  unitPriceGross: moneyAmountSchema,
  taxRateId: z.string().uuid(),
  discountAmount: nonNegativeMoneyAmountSchema.optional().default("0"),
  // Modul Normativ: izabrana varijacija i dodaci uz stavku (pamte se kao podaci, ne samo kao tekst u nazivu).
  variationId: z.string().uuid().nullable().optional(),
  addons: z
    .array(z.object({ optionId: z.string().uuid(), quantity: z.number().int().min(1).max(99).default(1) }))
    .max(30)
    .optional(),
});
export type AddFiscalDocumentItemInput = z.infer<
  typeof addFiscalDocumentItemSchema
>;

export const updateFiscalDocumentItemSchema = z.object({
  quantity: quantitySchema.optional(),
  discountAmount: nonNegativeMoneyAmountSchema.optional(),
});
export type UpdateFiscalDocumentItemInput = z.infer<
  typeof updateFiscalDocumentItemSchema
>;

export const registerPaymentSchema = z
  .object({
    method: z.nativeEnum(PaymentMethod),
    amount: moneyAmountSchema,
    receivedAmount: moneyAmountSchema.optional(),
  })
  .refine(
    (data) =>
      data.method !== PaymentMethod.CASH ||
      data.receivedAmount === undefined ||
      Number(data.receivedAmount) >= Number(data.amount),
    {
      message: "Primljeni iznos ne može biti manji od iznosa za naplatu",
      path: ["receivedAmount"],
    },
  );
export type RegisterPaymentInput = z.infer<typeof registerPaymentSchema>;

export const fiscalizeDocumentSchema = z.object({
  idempotencyKey: z.string().uuid(),
});
export type FiscalizeDocumentInput = z.infer<typeof fiscalizeDocumentSchema>;

// L-PFR tok (01.10.2026, vidi docs/timovi/esir-lpfr-integracija.md) - kasa
// prvo trazi pripremljen zahtev (prepareLpfrInvoiceSchema), sama ga salje
// L-PFR-u, pa Finori prijavljuje potpisan odgovor (confirmLpfrInvoiceSchema).
export const prepareLpfrInvoiceSchema = z.object({
  idempotencyKey: z.string().uuid(),
});
export type PrepareLpfrInvoiceInput = z.infer<typeof prepareLpfrInvoiceSchema>;

export const confirmLpfrInvoiceSchema = z.object({
  idempotencyKey: z.string().uuid(),
  fiscalNumber: z.string().min(1),
  invoiceCounter: z.string().optional(),
  qrCodeBase64: z.string().optional(),
  verificationUrl: z.string().optional(),
  journal: z.string().min(1),
  itemLabels: z.array(z.string()).optional(),
  rawResponse: z.unknown().optional(),
  // JID L-PFR uredjaja koji je potpisao racun (02.10.2026, stavka (3) iz
  // esir-lpfr-integracija.md, tacka 4) - salje se samo na PRVOM confirm-u
  // (prodaja), cuva se na dokumentu; ista sema se koristi i za
  // confirm-refund/confirm-copy gde se jid ignorise (uredjaj je vec
  // zabelezen na originalu).
  jid: z.string().optional(),
});
export type ConfirmLpfrInvoiceInput = z.infer<typeof confirmLpfrInvoiceSchema>;

// Odgovor mobilnog tima 01.10.2026 (esir-lpfr-integracija.md): tok nacrt ->
// stavka po stavka -> plaćanje -> prepare je 4+N poziva po računu. Ova ruta
// (POST /fiscal-documents/lpfr/prepare, BEZ :id) spaja sva četiri u jedan -
// isto telo kao stvaranje nacrta + stavke + plaćanje, vraća odmah pripremljen
// L-PFR zahtev. `idempotencyKey` ovde štiti i stvaranje nacrta (ponovljen
// poziv sa istim ključem ne pravi drugi dokument), ne samo samu pripremu.
export const prepareLpfrInvoiceBulkSchema = z.object({
  idempotencyKey: z.string().uuid(),
  /** Kasa koja izdaje račun (23.10.2026, zahtev mobilnog tima) - upisuje se na dokument da kasa vidi samo svoje račune. */
  fcmDeviceId: z.string().uuid().nullable().optional(),
  businessLocationId: z.string().uuid(),
  customerId: z.string().uuid().nullable().optional(),
  currency: z.string().length(3).default("RSD"),
  note: z.string().max(1000).nullable().optional(),
  pfrDocumentType: z
    .enum(["PROMET", "AVANS", "OBUKA", "KOPIJA", "PREDRACUN"])
    .optional(),
  /** Kupac bez kartice u bazi (29.10.2026, avans bez PIB-a): PIB/ID ide na PFR kao ID kupca. Prihvata i snake_case. */
  buyerPib: z.string().trim().max(32).nullable().optional(),
  buyer_pib: z.string().trim().max(32).nullable().optional(),
  buyerName: z.string().trim().max(255).nullable().optional(),
  buyer_name: z.string().trim().max(255).nullable().optional(),
  buyerIdType: z.string().trim().max(8).nullable().optional(),
  buyer_id_type: z.string().trim().max(8).nullable().optional(),
  buyerAddress: z.string().trim().max(255).nullable().optional(),
  buyer_address: z.string().trim().max(255).nullable().optional(),
  /** Opciono polje kupca, npr. "20:01234567" (samo Promet Prodaja). */
  buyerOptionalField: z.string().trim().max(64).nullable().optional(),
  buyer_optional_field: z.string().trim().max(64).nullable().optional(),
  /** Ime kasira za red „Касир:“ (kreator je API ključ, ne korisnik). */
  cashierName: z.string().trim().max(120).nullable().optional(),
  cashier_name: z.string().trim().max(120).nullable().optional(),
  items: z.array(addFiscalDocumentItemSchema).min(1, "Potrebna je bar jedna stavka"),
  payments: z.array(registerPaymentSchema).min(1, "Potrebno je bar jedno plaćanje"),
});
export type PrepareLpfrInvoiceBulkInput = z.infer<typeof prepareLpfrInvoiceBulkSchema>;

// Rok trajanja (dogovoreno 28.09.2026, vidi troskovi-modul... / artikli
// varijacije i rok trajanja): NONE = nema roka, DATE = tačan datum
// (shelfLifeDate, format GGGG-MM-DD), DAYS = broj dana od prijema
// (shelfLifeDays, koristi se samo kao predloženi default za
// GoodsReceiptItem.expiryDate prilikom prijema robe - ne menja postojeću
// logiku prijema).
const shelfLifeBaseSchema = z.object({
  name: z.string().min(1, "Naziv je obavezan").max(255),
  sku: z.string().max(64).optional(),
  barcode: z.string().max(64).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  unitPriceGross: moneyAmountSchema,
  taxRateId: z.string().uuid(),
  categoryId: z.string().uuid().nullable().optional(),
  unit: z.string().min(1).max(32).default("kom"),
  isService: z.boolean().default(false),
  purchasePrice: z.string().nullable().optional(),
  purchaseDiscountPercent: z.string().nullable().optional(),
  hasWarranty: z.boolean().optional().default(false),
  warrantyDuration: z.number().int().positive().nullable().optional(),
  warrantyUnit: z.enum(["YEARS", "MONTHS"]).optional().default("YEARS"),
  internalNote: z.string().max(2000).nullable().optional(),
  isFeatured: z.boolean().optional().default(false),
  allowDiscount: z.boolean().optional().default(true),
  showImageOnReceipt: z.boolean().optional().default(false),
  shelfLifeMode: z.enum(["NONE", "DATE", "DAYS"]).optional().default("NONE"),
  shelfLifeDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum mora biti u formatu GGGG-MM-DD")
    .nullable()
    .optional(),
  shelfLifeDays: z.number().int().positive().nullable().optional(),
  minStockQuantity: z.string().nullable().optional(),
  ignoreStockLimit: z.boolean().optional().default(false),
  metaTitle: z.string().max(255).nullable().optional(),
  shortDescription: z.string().max(500).nullable().optional(),
  tags: z.array(z.string().max(64)).optional().default([]),
  sortOrder: z.number().int().optional().default(0),
});

export const productFormSchema = shelfLifeBaseSchema.superRefine((data, ctx) => {
  if (data.shelfLifeMode === "DATE" && !data.shelfLifeDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Datum roka trajanja je obavezan",
      path: ["shelfLifeDate"],
    });
  }
  if (data.shelfLifeMode === "DAYS" && !data.shelfLifeDays) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Broj dana roka trajanja je obavezan",
      path: ["shelfLifeDays"],
    });
  }
});
export type ProductFormInput = z.infer<typeof productFormSchema>;
// Ulazni (pre-default) oblik iste šeme - koristi ga ProductsService.create()
// umesto ProductFormInput (izlazni/posle-default oblik), jer z.infer() na
// poljima sa .default(...) daje IZLAZNI tip u kom su ta polja OBAVEZNA (jer
// default uvek popuni vrednost), iako su na ulazu opciona. Interni pozivaoci
// (prijem robe/MiniMax sync/CSV-uvoz) prave "minimalan" artikal i namerno ne
// znaju/ne treba da znaju za polja kao što su shelfLifeMode/tags/isFeatured -
// ProductsService.create() već ima sve odgovarajuće `?? default` vrednosti u
// telu metode, pa je bezbedno da prihvata ovaj širi, ulazni oblik.
export type CreateProductServiceInput = z.input<typeof productFormSchema>;

export const updateProductSchema = shelfLifeBaseSchema.partial().superRefine((data, ctx) => {
  if (data.shelfLifeMode === "DATE" && !data.shelfLifeDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Datum roka trajanja je obavezan",
      path: ["shelfLifeDate"],
    });
  }
  if (data.shelfLifeMode === "DAYS" && !data.shelfLifeDays) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Broj dana roka trajanja je obavezan",
      path: ["shelfLifeDays"],
    });
  }
});
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

// --- Varijacije artikla i "ukloni sastojke" (28.09.2026) -------------------
// Varijacije su međusobno isključive, imaju sopstvenu cenu/barkod/sku i status
// - NAMERNO odvojeno od ProductAttributeGroup/ProductAttributeOption
// ("Dodaci", multi-select add-oni sa uticajem na cenu preko iznosa po stavci).
export const createProductVariationSchema = z.object({
  name: z.string().min(1, "Naziv varijacije je obavezan").max(255),
  priceModifier: moneyAmountSchema.optional(),
  barcode: z.string().max(64).nullable().optional(),
  sku: z.string().max(64).nullable().optional(),
});
export type CreateProductVariationInput = z.infer<
  typeof createProductVariationSchema
>;

export const updateProductVariationSchema = createProductVariationSchema
  .partial()
  .extend({
    status: z.enum(["ACTIVE", "ARCHIVED"]).optional(),
  });
export type UpdateProductVariationInput = z.infer<
  typeof updateProductVariationSchema
>;

// "Ukloni sastojke" - samo lista naziva bez uticaja na cenu, za buduću/
// posebnu POS obradu (ne dira postojeći Dodaci ekran).
export const createProductRemovableIngredientSchema = z.object({
  name: z.string().min(1, "Naziv sastojka je obavezan").max(255),
});
export type CreateProductRemovableIngredientInput = z.infer<
  typeof createProductRemovableIngredientSchema
>;

export const createUnitSchema = z.object({
  name: z.string().min(1, "Naziv jedinice mere je obavezan").max(32),
});
export type CreateUnitInput = z.infer<typeof createUnitSchema>;

// --- Faza 2: Ponude/Fakture (SalesDocument) --------------------------------

export const createSalesDocumentSchema = z.object({
  type: z.nativeEnum(SalesDocumentType),
  businessLocationId: z.string().uuid(),
  customerId: z.string().uuid().nullable().optional(),
  currency: z.string().length(3).default("RSD"),
  validUntilOrDueDate: z.string().datetime().nullable().optional(),
  paymentTerms: z.string().max(500).nullable().optional(),
  deliveryTerms: z.string().max(500).nullable().optional(),
  customerNote: z.string().max(2000).nullable().optional(),
  internalNote: z.string().max(2000).nullable().optional(),
});
export type CreateSalesDocumentInput = z.infer<
  typeof createSalesDocumentSchema
>;

export const addSalesDocumentItemSchema = z.object({
  productId: z.string().uuid().nullable().optional(),
  name: z.string().min(1, "Naziv stavke je obavezan").max(255),
  description: z.string().max(1000).nullable().optional(),
  quantity: quantitySchema,
  unit: z.string().min(1).max(16).default("kom"),
  unitPriceGross: moneyAmountSchema,
  taxRateId: z.string().uuid(),
  discountAmount: nonNegativeMoneyAmountSchema.optional().default("0"),
  purchasePriceGross: z.string().nullable().optional(),
  serialNumber: z.string().max(120).nullable().optional(),
});
export type AddSalesDocumentItemInput = z.infer<
  typeof addSalesDocumentItemSchema
>;

export const updateSalesDocumentItemSchema = z.object({
  quantity: quantitySchema.optional(),
  discountAmount: nonNegativeMoneyAmountSchema.optional(),
  unitPriceGross: moneyAmountSchema.optional(),
  taxRateId: z.string().uuid().optional(),
  serialNumber: z.string().max(120).nullable().optional(),
});
export type UpdateSalesDocumentItemInput = z.infer<
  typeof updateSalesDocumentItemSchema
>;

export const convertQuoteToInvoiceSchema = z.object({
  items: z
    .array(z.object({ itemId: z.string().uuid(), quantity: quantitySchema }))
    .optional(),
  invoiceType: z.nativeEnum(SalesDocumentType).optional(),
  idempotencyKey: z.string().uuid(),
});
export type ConvertQuoteToInvoiceInput = z.infer<
  typeof convertQuoteToInvoiceSchema
>;

export const registerSalesDocumentPaymentSchema = z.object({
  method: z.nativeEnum(PaymentMethod),
  amount: moneyAmountSchema,
  note: z.string().max(500).nullable().optional(),
});
export type RegisterSalesDocumentPaymentInput = z.infer<
  typeof registerSalesDocumentPaymentSchema
>;

export const sendSalesDocumentEmailSchema = z.object({
  to: z.string().email().optional(),
  message: z.string().max(2000).optional(),
});
export type SendSalesDocumentEmailInput = z.infer<typeof sendSalesDocumentEmailSchema>;

// --- Ponavljajuće fakture ---------------------------------------------------

export const createRecurringInvoiceTemplateSchema = z.object({
  businessLocationId: z.string().uuid(),
  customerId: z.string().uuid(),
  frequency: z.nativeEnum(RecurringFrequency),
  startDate: z.string().datetime().or(z.string().min(1)),
  endDate: z.string().nullable().optional(),
  autoSend: z.boolean().optional().default(false),
  paymentTerms: z.string().max(500).nullable().optional(),
  customerNote: z.string().max(2000).nullable().optional(),
  items: z
    .array(
      z.object({
        productId: z.string().uuid().nullable().optional(),
        name: z.string().min(1).max(255),
        quantity: quantitySchema,
        unitPriceGross: moneyAmountSchema,
        taxRateId: z.string().uuid(),
        discountAmount: nonNegativeMoneyAmountSchema.optional().default("0"),
      }),
    )
    .min(1, "Šablon mora imati bar jednu stavku"),
});
export type CreateRecurringInvoiceTemplateInput = z.infer<
  typeof createRecurringInvoiceTemplateSchema
>;

export const updateRecurringInvoiceTemplateSchema = z.object({
  active: z.boolean().optional(),
  autoSend: z.boolean().optional(),
  endDate: z.string().nullable().optional(),
});
export type UpdateRecurringInvoiceTemplateInput = z.infer<
  typeof updateRecurringInvoiceTemplateSchema
>;

// --- Bankovni izvodi ---------------------------------------------------------

export const bookBankStatementLineSchema = z.object({
  documentId: z.string().uuid(),
});
export type BookBankStatementLineInput = z.infer<typeof bookBankStatementLineSchema>;

export const updateReconciliationModeSchema = z.object({
  reconciliationMode: z.nativeEnum(ReconciliationMode),
});
export type UpdateReconciliationModeInput = z.infer<
  typeof updateReconciliationModeSchema
>;

// --- SEF (eFakture) ----------------------------------------------------------

export const updateSefSettingsSchema = z.object({
  apiKey: z.string().min(1).max(255).optional(),
  sefMode: z.nativeEnum(SefMode).optional(),
  sefSendToCir: z.boolean().optional(),
});
export type UpdateSefSettingsInput = z.infer<typeof updateSefSettingsSchema>;

export const cancelSefInvoiceSchema = z.object({
  cancelComment: z.string().min(1, "Razlog otkazivanja je obavezan").max(500),
});
export type CancelSefInvoiceInput = z.infer<typeof cancelSefInvoiceSchema>;

// --- Podešavanja kompanije ---------------------------------------------------

export const updateCompanySettingsSchema = z.object({
  name: z.string().min(1, "Naziv firme je obavezan").max(255).optional(),
  taxId: z.string().min(1, "PIB je obavezan").max(32).optional(),
  registrationNumber: z.string().min(1, "Matični broj je obavezan").max(32).optional(),
  businessLocationName: z.string().min(1).max(255).optional(),
  businessLocationAddress: z.string().min(1).max(255).optional(),
  isVatRegistered: z.boolean().optional(),
  showProductImagesOnQuotes: z.boolean().optional(),
  showProductImagesOnInvoices: z.boolean().optional(),
  bankAccount: z.string().min(1).max(64).optional(),
  skuFormat: z.nativeEnum(SkuFormat).optional(),
  invoiceNumberPrefix: z.string().min(1).max(10).optional(),
  quoteNumberPrefix: z.string().min(1).max(10).optional(),
  deliveryNoteNumberPrefix: z.string().min(1).max(10).optional(),
  includeYearInNumber: z.boolean().optional(),
  invoiceNumberStart: z.number().int().min(1).max(999999).nullable().optional(),
  quoteNumberStart: z.number().int().min(1).max(999999).nullable().optional(),
  showLogoOnReceipt: z.boolean().optional(),
  posQuantityMode: z.nativeEnum(PosQuantityMode).optional(),
  emailTemplateDesign: z.nativeEnum(EmailTemplateDesign).optional(),
  logoUrl: z.string().optional(),
  quoteAcceptanceText: z.string().max(2000).optional(),
  defaultQuoteNote: z.string().max(2000).optional(),
  defaultQuotePaymentTerms: z.string().max(2000).optional(),
  defaultInvoiceNote: z.string().max(2000).optional(),
  defaultInvoicePaymentTerms: z.string().max(2000).optional(),
  defaultDeliveryNoteNote: z.string().max(2000).optional(),
  autoSendToSefOnAcceptance: z.boolean().optional(),
  defaultCombinedPaymentMethod: z.nativeEnum(PaymentMethod).optional(),
  usesWarehouse: z.boolean().optional(),
  esirId: z.string().max(64).optional(),
  // "Moj profil" - adresa/kontakt firme (30.09.2026).
  street: z.string().max(255).optional(),
  houseNumber: z.string().max(32).optional(),
  postalCode: z.string().max(16).optional(),
  city: z.string().max(120).optional(),
  phone: z.string().max(32).optional(),
});
export type UpdateCompanySettingsInput = z.infer<typeof updateCompanySettingsSchema>;

// Super-admin - izmena istih "Moj profil" polja NA TUĐOJ firmi (companyId iz
// URL-a, ne iz tenant konteksta) - vidi CompanyDetailPage.tsx OverviewTab.
export const updateCompanyProfileAdminSchema = z.object({
  street: z.string().max(255).optional(),
  houseNumber: z.string().max(32).optional(),
  postalCode: z.string().max(16).optional(),
  city: z.string().max(120).optional(),
  phone: z.string().max(32).optional(),
});
export type UpdateCompanyProfileAdminInput = z.infer<typeof updateCompanyProfileAdminSchema>;

// Finora Admin -> firma -> korisnici: izmena imena, emaila, lozinke i statusa naloga.
export const updateCompanyUserAdminSchema = z.object({
  fullName: z.string().min(1, "Ime i prezime su obavezni").max(255).optional(),
  email: z.string().email("Unesite ispravnu email adresu").optional(),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera").max(128).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateCompanyUserAdminInput = z.infer<typeof updateCompanyUserAdminSchema>;

export const updateSmtpSettingsSchema = z.object({
  smtpHost: z.string().min(1).max(255).optional(),
  smtpPort: z.number().int().min(1).max(65535).optional(),
  smtpUser: z.string().min(1).max(255).optional(),
  smtpPass: z.string().min(1).max(255).optional(),
  smtpFrom: z.string().email().optional().or(z.literal("")),
  smtpSecure: z.boolean().optional(),
});
export type UpdateSmtpSettingsInput = z.infer<typeof updateSmtpSettingsSchema>;

export const updatePfrSettingsSchema = z.object({
  pfrMode: z.nativeEnum(PfrMode).optional(),
  pfrBaseUrl: z.string().url().optional().or(z.literal("")),
  pfrCertificatePassword: z.string().min(1).max(255).optional(),
  pfrApiKey: z.string().min(1).max(255).optional(),
});
export type UpdatePfrSettingsInput = z.infer<typeof updatePfrSettingsSchema>;

// --- IPS QR naplata (Payten PGW) - vidi razgovor, Podešavanja -> IPS QR Naplata ---
export const updateIpsPgwSettingsSchema = z.object({
  ipsPgwEnabled: z.boolean().optional(),
  ipsPgwEnvironment: z.enum(["TEST", "PRODUCTION"]).optional(),
  ipsPgwTid: z.string().min(1).max(32).optional(),
  ipsPgwUserId: z.string().min(1).max(255).optional(),
  ipsPgwMcc: z.string().min(1).max(16).optional(),
});
export type UpdateIpsPgwSettingsInput = z.infer<typeof updateIpsPgwSettingsSchema>;

// --- Naplata karticama (OTP Banka VPOS) - vidi razgovor "modul za kartice za otp za finoru" ---
export const updateOtpCardSettingsSchema = z.object({
  otpCardEnabled: z.boolean().optional(),
  otpCardEnvironment: z.enum(["TEST", "PRODUCTION"]).optional(),
  otpCardStoreName: z.string().min(1).max(64).optional(),
  otpCardCurrency: z.string().min(1).max(8).optional(),
  otpCardSharedSecret: z.string().min(1).max(255).optional(),
  otpCardSharedSecretApi: z.string().min(1).max(255).optional(),
});
export type UpdateOtpCardSettingsInput = z.infer<typeof updateOtpCardSettingsSchema>;

export const updateIntesaCardSettingsSchema = z.object({
  intesaCardEnabled: z.boolean().optional(),
  intesaCardEnvironment: z.enum(["TEST", "PRODUCTION"]).optional(),
  intesaCardClientId: z.string().min(1).max(64).optional(),
  intesaCardStoreKey: z.string().min(1).max(255).optional(),
  intesaCardCurrency: z.string().min(1).max(8).optional(),
});
export type UpdateIntesaCardSettingsInput = z.infer<typeof updateIntesaCardSettingsSchema>;

export const updateBexCourierSettingsSchema = z.object({
  bexEnabled: z.boolean().optional(),
  bexApiKey: z.string().min(1).max(255).optional(),
  bexClientId: z.string().min(1).max(64).optional(),
  bexSenderAccount: z.string().min(1).max(64).optional(),
  bexSenderPhone: z.string().min(1).max(32).optional(),
  bexSenderContactPerson: z.string().min(1).max(128).optional(),
});
export type UpdateBexCourierSettingsInput = z.infer<typeof updateBexCourierSettingsSchema>;

export const updatePostaCourierSettingsSchema = z.object({
  postaEnabled: z.boolean().optional(),
  postaEnvironment: z.enum(["TEST", "PRODUCTION"]).optional(),
  postaUsername: z.string().min(1).max(64).optional(),
  postaPassword: z.string().min(1).max(255).optional(),
  postaSenderAccount: z.string().min(1).max(64).optional(),
  postaSenderPhone: z.string().min(1).max(32).optional(),
  postaSenderContactPerson: z.string().min(1).max(128).optional(),
});
export const updateCityExpressCourierSettingsSchema = z.object({
  cityExpressEnabled: z.boolean().optional(),
  cityExpressApiKey: z.string().min(8).max(128).optional(),
  cityExpressSenderId: z.string().regex(/^\d{1,10}$/, "ID pošiljaoca mora biti broj").optional(),
});
export const updateShipmentNotificationSettingsSchema = z.object({
  notifyOnDispatch: z.boolean().optional(),
  notifyOnStatus: z.boolean().optional(),
});
export type UpdateShipmentNotificationSettingsInput = z.infer<typeof updateShipmentNotificationSettingsSchema>;
export type UpdateCityExpressCourierSettingsInput = z.infer<typeof updateCityExpressCourierSettingsSchema>;
export type UpdatePostaCourierSettingsInput = z.infer<typeof updatePostaCourierSettingsSchema>;

export const dispatchShipmentSchema = z.object({
  weightKg: z.number().positive().max(1000).optional(),
  totalPackages: z.number().int().positive().max(50).optional(),
  codAmount: z.number().min(0).optional(),
  comment: z.string().max(255).optional(),
});
export type DispatchShipmentInput = z.infer<typeof dispatchShipmentSchema>;

export const updateRaiffeisenCardSettingsSchema = z.object({
  raiffeisenCardEnabled: z.boolean().optional(),
  raiffeisenCardEnvironment: z.enum(["TEST", "PRODUCTION"]).optional(),
  raiffeisenCardMerchantId: z.string().min(1).max(32).optional(),
  raiffeisenCardTerminalId: z.string().min(1).max(32).optional(),
  // PEM format - veliki tekst (privatni kljuc / bankin sertifikat), ne
  // kratak secret kao kod OTP/Intesa - vidi raiffeisen-card.service.ts.
  raiffeisenCardPrivateKeyPem: z.string().min(1).max(16000).optional(),
  raiffeisenCardBankCertPem: z.string().min(1).max(16000).optional(),
  raiffeisenCardCurrency: z.string().min(1).max(8).optional(),
});
export type UpdateRaiffeisenCardSettingsInput = z.infer<typeof updateRaiffeisenCardSettingsSchema>;

export const customerFormSchema = z.object({
  name: z.string().min(1, "Naziv/ime kupca je obavezan").max(255),
  taxId: z.string().max(32).nullable().optional(),
  registrationNumber: z.string().max(32).nullable().optional(),
  jbkjs: z.string().max(32).nullable().optional(),
  isVatRegistered: z.boolean().optional().default(true),
  hasEInvoice: z.boolean().optional().default(false),
  email: z.string().email().nullable().optional().or(z.literal("")),
  phone: z.string().max(32).nullable().optional(),
  address: z.string().max(255).nullable().optional(),
  houseNumber: z.string().max(32).nullable().optional(),
  postalCode: z.string().max(16).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  buyerIdType: z.string().max(4).nullable().optional(),
  buyerIdValue: z.string().max(64).nullable().optional(),
});
export type CustomerFormInput = z.infer<typeof customerFormSchema>;

export const updateCustomerSchema = customerFormSchema.partial();
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

// --- Otpremnica ----------------------------------------------------------

export const confirmDeliveryNoteSchema = z.object({
  confirmedByName: z.string().min(1, "Ime je obavezno").max(100),
  confirmedBySurname: z.string().min(1, "Prezime je obavezno").max(100),
  confirmedByIdCardNumber: z
    .string()
    .regex(/^\d{9}$/, "Broj lične karte mora imati tačno 9 cifara"),
  confirmedByIdCardIssuePlace: z.string().min(1, "Mesto izdavanja je obavezno").max(100),
  confirmedNote: z.string().max(2000).optional(),
});
export type ConfirmDeliveryNoteInput = z.infer<typeof confirmDeliveryNoteSchema>;

// --- Prijem robe: magacin, dobavljači, ulaz robe -----------------------------

export const warehouseFormSchema = z.object({
  name: z.string().min(1, "Naziv magacina je obavezan").max(255),
  isPartOfBusinessLocation: z.boolean().default(true),
  businessLocationId: z.string().uuid().nullable().optional(),
  address: z.string().max(255).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  postalCode: z.string().max(16).nullable().optional(),
});
export type WarehouseFormInput = z.infer<typeof warehouseFormSchema>;
export const updateWarehouseSchema = warehouseFormSchema.partial();
export type UpdateWarehouseInput = z.infer<typeof updateWarehouseSchema>;

export const supplierFormSchema = z.object({
  name: z.string().min(1, "Naziv dobavljača je obavezan").max(255),
  address: z.string().max(255).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  postalCode: z.string().max(16).nullable().optional(),
  email: z.string().email().nullable().optional().or(z.literal("")),
  phone: z.string().max(32).nullable().optional(),
  website: z.string().max(255).nullable().optional(),
  taxId: z.string().max(32).nullable().optional(),
  registrationNumber: z.string().max(32).nullable().optional(),
  bankAccount: z.string().max(64).nullable().optional(),
});
export type SupplierFormInput = z.infer<typeof supplierFormSchema>;
export const updateSupplierSchema = supplierFormSchema.partial();
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

export const goodsReceiptItemInputSchema = z.object({
  productId: z.string().uuid().nullable().optional(),
  sku: z.string().max(64).optional(),
  barcode: z.string().max(64).optional(),
  serialNumber: z.string().max(120).optional(),
  name: z.string().min(1, "Naziv stavke je obavezan").max(255),
  quantity: quantitySchema,
  unitPrice: moneyAmountSchema,
  discountPercent: z.string().optional().default("0"),
  taxRatePercent: z.string().optional().default("20"),
  expiryDate: z.string().nullable().optional(),
});

export const createGoodsReceiptSchema = z.object({
  warehouseId: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierDeliveryNoteNumber: z.string().max(64).optional(),
  receiptDate: z.string().optional(),
  dueDate: z.string().optional(),
  items: z.array(goodsReceiptItemInputSchema).min(1, "Unesite bar jednu stavku"),
});
export type CreateGoodsReceiptInput = z.infer<typeof createGoodsReceiptSchema>;

// --- Autentifikacija i korisnici --------------------------------------------

export const loginSchema = z.object({
  email: z.string().email("Unesite ispravnu email adresu"),
  password: z.string().min(1, "Lozinka je obavezna"),
});
export type LoginInput = z.infer<typeof loginSchema>;

// Signup od sada NAMERNO ne traži firmu ni lozinku (vidi razgovor "za
// registraciju me nisi poslusao ... samo email i ime") - firma i lozinka se
// dodaju kasnije, u confirmAccountSchema (lozinka, preko mail linka) i
// onboardingCreateCompanySchema (firma, u wizardu).
export const signupSchema = z.object({
  fullName: z.string().min(1, "Ime i prezime su obavezni").max(255),
  email: z.string().email("Unesite ispravnu email adresu"),
});
export type SignupInput = z.infer<typeof signupSchema>;

export const confirmAccountSchema = z.object({
  token: z.string().min(1, "Token je obavezan"),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera"),
});
export type ConfirmAccountInput = z.infer<typeof confirmAccountSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email("Unesite ispravnu email adresu"),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(1, "Token je obavezan"),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera"),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// Samostalna promena lozinke (ulogovan korisnik, "Nalog"/"Bezbednost" tab u
// Podešavanjima) - 30.09.2026. Ista jačina lozinke kao signup/reset (min 6).
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Trenutna lozinka je obavezna"),
  newPassword: z.string().min(6, "Lozinka mora imati bar 6 karaktera"),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// Prijava preko OTP koda na mejl - vidi razgovor "korisnik moze za login da
// bira hoce li otp ili sifru". Alternativa loginSchema (lozinka), ista JWT
// na kraju (vidi AuthService.buildLoginResponse).
export const otpRequestSchema = z.object({
  email: z.string().email("Unesite ispravnu email adresu"),
});
export type OtpRequestInput = z.infer<typeof otpRequestSchema>;

export const otpVerifySchema = z.object({
  email: z.string().email("Unesite ispravnu email adresu"),
  code: z.string().regex(/^\d{6}$/, "Kod mora imati tačno 6 cifara"),
});
export type OtpVerifyInput = z.infer<typeof otpVerifySchema>;

// NFC kartica login (Finora Mobile) - vidi razgovor "nfc kartice" 25.09.2026.
// PIN je namerno string (ne broj) - cuva vodece nule, i tako se salje/prima
// svuda (bcrypt hash na backend-u, tastatura na mobilnom).
export const cardLoginSchema = z.object({
  cardSerial: z.string().min(1, "Serijski broj kartice je obavezan").max(255),
  pin: z.string().regex(/^\d{4,8}$/, "PIN mora imati 4 do 8 cifara"),
  // Opciono - koji fizicki uredjaj/kiosk salje login ("Dopuna
  // specifikacije" 25.09.2026). Trenutno se samo prima i prosledjuje
  // dalje - jos ne postoji logika koja ga koristi.
  deviceId: z.string().max(255).optional(),
});
export type CardLoginInput = z.infer<typeof cardLoginSchema>;

export const assignEmployeeCardSchema = z.object({
  cardSerial: z.string().min(1, "Serijski broj kartice je obavezan").max(255),
  pin: z.string().regex(/^\d{4,8}$/, "PIN mora imati 4 do 8 cifara"),
});
export type AssignEmployeeCardInput = z.infer<typeof assignEmployeeCardSchema>;

// Kreiranje firme u onboarding "Kompanija" koraku - isti podaci koje je
// RANIJE tražio signup, samo sad ih unosi korisnik koji već ima potvrđen
// nalog bez firme (vidi CompanyController.createForOnboarding).
export const onboardingCreateCompanySchema = z.object({
  companyName: z.string().min(1, "Naziv firme je obavezan").max(255),
  taxId: z.string().min(1, "PIB je obavezan").max(32),
  registrationNumber: z.string().min(1, "Matični broj je obavezan").max(32),
  address: z.string().min(1, "Adresa je obavezna").max(255),
  isVatRegistered: z.boolean().optional(),
  bankAccounts: z
    .array(z.object({ accountNumber: z.string().min(1).max(64), bankName: z.string().max(255).nullable().optional() }))
    .max(20)
    .optional(),
});
export type OnboardingCreateCompanyInput = z.infer<typeof onboardingCreateCompanySchema>;

// Finora Admin -> "Nova firma" (super-admin ručno pravi firmu + vlasnika u
// jednom koraku) - NAMERNO odvojeno od signupSchema (koji je otkad je
// javna registracija svedena na samo email+ime prekratak za ovaj slučaj:
// ovde admin odmah unosi i lozinku i sve podatke firme, korisnik se ne
// potvrđuje preko mejla). Vidi SuperAdminService.createCompany.
export const createCompanyAsAdminSchema = z.object({
  companyName: z.string().min(1, "Naziv firme je obavezan").max(255),
  taxId: z.string().min(1, "PIB je obavezan").max(32),
  registrationNumber: z.string().min(1, "Matični broj je obavezan").max(32),
  address: z.string().min(1, "Adresa je obavezna").max(255),
  fullName: z.string().min(1, "Ime i prezime su obavezni").max(255),
  email: z.string().email("Unesite ispravnu email adresu"),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera"),
  isVatRegistered: z.boolean().optional(),
  bankAccounts: z
    .array(z.object({ accountNumber: z.string().min(1).max(64), bankName: z.string().max(255).nullable().optional() }))
    .max(20)
    .optional(),
});
export type CreateCompanyAsAdminInput = z.infer<typeof createCompanyAsAdminSchema>;

export const createUserSchema = z.object({
  email: z.string().email("Unesite ispravnu email adresu"),
  fullName: z.string().min(1, "Ime i prezime su obavezni").max(255),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera"),
  role: z.nativeEnum(CompanyUserRole),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  fullName: z.string().min(1).max(255).optional(),
  role: z.nativeEnum(CompanyUserRole).optional(),
  isActive: z.boolean().optional(),
  password: z.string().min(6, "Lozinka mora imati bar 6 karaktera").optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/**
 * RBAC - upis individualnih override-a dozvola za jednog korisnika
 * (vidi CompanyUserPermission u schema.prisma i PermissionsGuard).
 * `true`/`false` postavlja eksplicitan override; `null` BRIŠE override
 * (korisnik se vraća na role default za taj ključ) - namerno, da UI
 * kasnije može da ima "vrati na podrazumevano" bez posebnog endpoint-a.
 */
export const updateUserPermissionsSchema = z.object({
  permissions: z
    .record(z.enum(PERMISSION_KEYS), z.boolean().nullable())
    .refine((obj) => Object.keys(obj).length > 0, {
      message: "Bar jedna dozvola mora biti prosleđena.",
    }),
});
export type UpdateUserPermissionsInput = z.infer<
  typeof updateUserPermissionsSchema
>;

// ---------------------------------------------------------------------------
// Finora Cloud API (v1) - partnerski API za WooCommerce/WHMCS/Shopify/POS i
// sl. (vidi docs/CLOUD_API.md). NAMERNO snake_case (za razliku od ostatka
// ove datoteke) jer je to eksterni, javno dokumentovan ugovor koji spoljni
// sistemi implementiraju - menjati oblik ovih polja je BREAKING CHANGE za
// partnere, ne samo interni refactoring.
// ---------------------------------------------------------------------------

export const cloudApiFiscalizeItemSchema = z.object({
  name: z.string().min(1, "Naziv stavke je obavezan").max(255),
  quantity: quantitySchema,
  unit_price_gross: moneyAmountSchema,
  // Poreska stopa kao procenat (npr. "20"), NE taxRateId - Cloud API klijent
  // ne zna (i ne treba da zna) interne ID-jeve poreskih stopa. Servisni sloj
  // (CloudApiFiscalizeService) je mapira na postojeću TaxRate firme.
  tax_rate: z.string().regex(/^\d+(\.\d{1,2})?$/, "tax_rate mora biti procenat, npr. \"20\""),
  discount_amount: nonNegativeMoneyAmountSchema.optional().default("0"),
  // Veza stavke sa artiklom iz HAFiskala (30.10.2026): GTIN/barkod ide Poreskoj. Dovoljno je jedno od dva.
  product_id: z.string().uuid().optional(),
  gtin: z.string().trim().max(32).optional(),
});

export const cloudApiPaymentSchema = z.object({
  method: z.nativeEnum(PaymentMethod),
  amount: moneyAmountSchema,
});

export const cloudApiFiscalizeSchema = z.object({
  external_id: z
    .string()
    .min(1, "external_id je obavezan (npr. broj narudžbine u vašem sistemu)")
    .max(191),
  business_location_id: z.string().uuid().optional(),
  currency: z.string().length(3).default("RSD"),
  customer_id: z.string().uuid().optional(),
  fcm_device_id: z.string().uuid().optional(),
  note: z.string().max(1000).optional(),
  // Avans (Mobile POS zahtev 23.09.2026) - "NORMAL" (podrazumevano, nazad-
  // kompatibilno sa partnerima koji ovo polje ne šalju) ili "ADVANCE".
  // Servisni sloj (CloudApiFiscalizeService) mapira "ADVANCE" na interni
  // pfrDocumentType="AVANS" (FiscalDocumentsService.update). Avansni iznos
  // se paketuje kao OBIČNA stavka u `items` (npr. naziv "Аванс", quantity
  // "1", unit_price_gross = traženi iznos, tax_rate = tražena stopa) - NEMA
  // posebnog polja za to, isti obrazac kao dashboard ručni unos avansa.
  invoice_type: z.enum(["NORMAL", "ADVANCE"]).optional().default("NORMAL"),
  // Dodatni tekst za DRUGI RED u polju "ID kupca" na racunu (03.10.2026,
  // MerchantPro - serijski broj prodate opreme). Opciono, NIJE deo zvanicnog
  // PFR sifarnika za ID kupca - vidi napomenu uz FiscalDocument.buyerIdExtra
  // u schema.prisma i resolveBuyerId() u fiscal-document.mapper.ts.
  buyer_id_extra: z.string().max(64).optional(),
  // Kupac (PIB/JMBG...) i ime kasira od kase (29.10.2026): avans i račun iz kase
  // moraju imati PIB kupca na računu i ime kasira. buyer_id_type = PFR vrsta ID-a
  // ("10" = PIB, podrazumevano); cashier_name se prikazuje u redu "Касир:".
  buyer_pib: z.string().trim().max(32).optional(),
  buyer_name: z.string().trim().max(255).optional(),
  buyer_id_type: z.string().trim().max(4).optional(),
  buyer_address: z.string().trim().max(255).optional(),
  buyer_optional_field: z.string().trim().max(64).optional(),
  cashier_name: z.string().trim().max(120).optional(),
  // MerchantPro dropship/mejl dogovor (03.10.2026) - kad je true, OVAJ
  // dokument se NE šalje na automatsku štampu (MerchantProSyncService.ts)
  // čak i kad poslovna lokacija ima autoPrintEnabled=true - vidi
  // FiscalDocument.autoPrintSuppressed u schema.prisma. Ostale integracije
  // ovo polje ne šalju, default false - bez promene postojećeg ponašanja.
  suppress_auto_print: z.boolean().optional(),
  // Popust na ceo račun (18.10.2026, zahtev mobilnog tima): iznos u dinarima sa porezom,
  // server ga srazmerno raspodeli na stavke (poslednja stavka dobija ostatak).
  // Opciono; bez njega ponašanje je isto kao ranije.
  discount_amount: nonNegativeMoneyAmountSchema.optional().default("0"),
  items: z.array(cloudApiFiscalizeItemSchema).min(1, "Račun mora imati bar jednu stavku"),
  payments: z.array(cloudApiPaymentSchema).min(1, "Račun mora imati bar jedno plaćanje"),
});
export type CloudApiFiscalizeInput = z.infer<typeof cloudApiFiscalizeSchema>;

export const cloudApiRefundSchema = z.object({
  idempotency_key: z.string().min(1).max(191),
});
export type CloudApiRefundInput = z.infer<typeof cloudApiRefundSchema>;

/**
 * POST /v1/fiscal-documents/:id/retry (26.09.2026) - bezbedan ponovni pokušaj
 * neuspele fiskalizacije (cron u WHMCS/WooCommerce/Magento modulima). Vidi
 * FiscalDocumentsService.retryFiscalization: pre slanja se kod PFR-a proverava
 * da li je raniji pokušaj ipak izdat račun. `include_validation_errors` =
 * ponovi i posle VALIDATION_ERROR (samo ručna akcija posle ispravke podešavanja).
 */
export const cloudApiRetrySchema = z.object({
  include_validation_errors: z.boolean().optional().default(false),
});
export type CloudApiRetryInput = z.infer<typeof cloudApiRetrySchema>;

/**
 * Licenca preko Cloud API-ja (27.09.2026) - vidi CloudApiLicenseService.
 * POST /v1/license/upgrade-request: zahtev za proširenje licence (npr.
 * WHMCS eFaktura dugme "Proširi licencu").
 */
export const cloudApiLicenseUpgradeSchema = z.object({
  module_key: z.string().min(1).max(64),
  note: z.string().max(500).optional(),
});
export type CloudApiLicenseUpgradeInput = z.infer<typeof cloudApiLicenseUpgradeSchema>;

/** POST /v1/license/usage - prijava upotrebe modula koji radi van Finore (npr. eFaktura iz WHMCS-a direktno na SEF). */
export const cloudApiLicenseUsageSchema = z.object({
  module_key: z.string().min(1).max(64),
  event_type: z.string().min(1).max(64),
  reference_id: z.string().min(1).max(150),
  quantity: z.number().positive().max(10000).optional().default(1),
});
export type CloudApiLicenseUsageInput = z.infer<typeof cloudApiLicenseUsageSchema>;

/** POST /v1/fiscal-documents/:id/close-advance (Mobile POS - Task #38 preko Cloud API-ja). */
export const cloudApiCloseAdvanceSchema = z.object({
  idempotency_key: z.string().min(1).max(191),
});
export type CloudApiCloseAdvanceInput = z.infer<typeof cloudApiCloseAdvanceSchema>;

/**
 * POST /v1/fiscal-documents/:id/complete-cash-sale (Mobile POS) - naplata +
 * fiskalizacija POSTOJEĆEG nacrt-dokumenta (koraka 3 zatvaranja avansa,
 * kreiranog preko close-advance) - za razliku od /v1/fiscalize, ovaj
 * endpoint NE PRAVI novi dokument, samo dovršava već postojeći.
 *
 * `items` (24.09.2026, Mobile bag): nacrt kreiran preko close-advance je
 * NAMERNO prazan (vidi closeAdvanceWithCashSale) - kasa mora ovde da upiše
 * prave stavke pre naplate, inače fiscalize() puca na FISCAL_DOCUMENT_EMPTY.
 * Isti oblik stavke kao /v1/fiscalize (cloudApiFiscalizeItemSchema) - kasa
 * već šalje tačno taj JSON. Opciono (a ne .min(1)) jer dokument može već
 * imati stavke iz ranijeg (npr. delimično uspelog) poziva - servisni sloj
 * upisuje ih samo ako je dokument još prazan (idempotentnost).
 */
export const cloudApiCompleteCashSaleSchema = z.object({
  idempotency_key: z.string().min(1).max(191),
  items: z.array(cloudApiFiscalizeItemSchema).optional(),
  payments: z.array(cloudApiPaymentSchema).min(1, "Račun mora imati bar jedno plaćanje"),
});
export type CloudApiCompleteCashSaleInput = z.infer<typeof cloudApiCompleteCashSaleSchema>;

export const cloudApiCustomerSchema = z.object({
  name: z.string().min(1, "Naziv/ime kupca je obavezan").max(255),
  tax_id: z.string().max(32).nullable().optional(),
  registration_number: z.string().max(32).nullable().optional(),
  is_vat_registered: z.boolean().optional().default(true),
  email: z.string().email().nullable().optional().or(z.literal("")),
  phone: z.string().max(32).nullable().optional(),
  address: z.string().max(255).nullable().optional(),
  external_id: z.string().max(191).optional(),
});
export type CloudApiCustomerInput = z.infer<typeof cloudApiCustomerSchema>;

export const cloudApiRegisterIpSchema = z.object({
  label: z.string().max(255).optional(),
});
export type CloudApiRegisterIpInput = z.infer<typeof cloudApiRegisterIpSchema>;

export const cloudApiHeartbeatSchema = z.object({
  device_id: z.string().uuid(),
});
export type CloudApiHeartbeatInput = z.infer<typeof cloudApiHeartbeatSchema>;

// --- Cloud administracija (dashboard - upravljanje integracijama) --------

export const createApiIntegrationSchema = z.object({
  name: z.string().min(1, "Naziv integracije je obavezan").max(255),
  type: z.enum(["WOOCOMMERCE", "WHMCS", "MAGENTO", "SHOPIFY", "POS", "ERP", "CUSTOM", "BANK_IMPORT"]),
  businessLocationId: z.string().uuid().nullable().optional(),
});
export type CreateApiIntegrationInput = z.infer<typeof createApiIntegrationSchema>;

export const updateApiIntegrationLocationSchema = z.object({
  businessLocationId: z.string().uuid().nullable().optional(),
});
export type UpdateApiIntegrationLocationInput = z.infer<typeof updateApiIntegrationLocationSchema>;

export const createWebhookEndpointSchema = z.object({
  integrationId: z.string().uuid().nullable().optional(),
  url: z.string().url("URL mora biti validna https adresa"),
  events: z
    .array(
      z.enum([
        "FISCALIZATION_COMPLETED",
        "FISCALIZATION_FAILED",
        "DOCUMENT_CANCELLED",
        "DOCUMENT_REFUNDED",
        "IPS_PAYMENT_PAID",
        "IPS_PAYMENT_FAILED",
      ]),
    )
    .min(1, "Izaberite bar jedan event"),
});
export type CreateWebhookEndpointInput = z.infer<typeof createWebhookEndpointSchema>;

// --- MiniMax integracija (docs/MINIMAX_INTEGRATION.md) ---------------------

export const minimaxSaveCredentialsSchema = z.object({
  clientId: z.string().min(1, "Client ID je obavezan"),
  clientSecret: z.string().min(1, "Client Secret je obavezan"),
});
export type MinimaxSaveCredentialsInput = z.infer<typeof minimaxSaveCredentialsSchema>;

export const minimaxConnectSchema = z.object({
  username: z.string().min(1, "MiniMax korisničko ime je obavezno"),
  password: z.string().min(1, "MiniMax lozinka je obavezna"),
});
export type MinimaxConnectInput = z.infer<typeof minimaxConnectSchema>;

export const minimaxSelectOrganisationSchema = z.object({
  organisationId: z.string().min(1),
  organisationName: z.string().min(1),
});
export type MinimaxSelectOrganisationInput = z.infer<typeof minimaxSelectOrganisationSchema>;

export const minimaxSelectWarehouseSchema = z.object({
  warehouseId: z.string().min(1),
  warehouseName: z.string().min(1),
});
export type MinimaxSelectWarehouseInput = z.infer<typeof minimaxSelectWarehouseSchema>;

// "Obrada izvoda" (n8n mail-import) - odredišni mejl za obrađen bankovni
// izvod (XML), vidi "Obrada izvoda - MiniMax odredište iz Finore i zaštita
// servisa" (22.09.2026). Prazan string briše polje (vraća se na null) - isti
// obrazac kao ostala opciona podešavanja u ovom modulu.
export const minimaxUpdateStatementEmailSchema = z.object({
  statementEmail: z
    .string()
    .trim()
    .toLowerCase()
    .email("Email adresa nije ispravna")
    .max(255)
    .optional()
    .or(z.literal("")),
});
export type MinimaxUpdateStatementEmailInput = z.infer<typeof minimaxUpdateStatementEmailSchema>;

export const bankStatementImportSchema = z.object({
  source: z.string().min(1, "source je obavezan"),
  job_id: z.string().min(1, "job_id je obavezan"),
  bank: z.string().min(1, "bank je obavezan"),
  // Obavezno SAMO kad se autentifikacija radi preko sistemskog (ne
  // po-firmi) API ključa - format PIB_BROJRACUNA@import.ha.co.rs, vidi
  // BankStatementImportService.resolveCompanyFromEmail. Preko per-firma
  // ključa firma je već poznata iz samog ključa, pa ovo polje nije bitno.
  import_email: z.string().nullable().optional(),
  // 23.09.2026 ("Obrada izvoda" mail-import v88 sada uvozi i u Izvode) -
  // alternativa za import_email kad pošiljalac (n8n) već pouzdano zna PIB
  // (npr. iz X-Finora-PIB header-a, ne iz izmišljene import_email adrese
  // koja ovde ništa realno ne predstavlja). Kad je prisutan, ima prednost
  // nad import_email. Vidi BankStatementImportService.import().
  pib: z.string().nullable().optional(),
  account: z.object({
    account_number: z.string().min(1, "account.account_number je obavezan"),
    currency: z.string().length(3).optional(),
    iban: z.string().nullable().optional(),
  }),
  statement: z.object({
    statement_number: z.string().nullable().optional(),
    period_from: z.string().min(1, "statement.period_from je obavezan"),
    period_to: z.string().min(1, "statement.period_to je obavezan"),
    opening_balance: z.number().optional(),
    closing_balance: z.number().optional(),
  }),
  transactions: z
    .array(
      z.object({
        transaction_id: z.string().nullable().optional(),
        date: z.string().min(1, "transactions[].date je obavezan"),
        value_date: z.string().nullable().optional(),
        description: z.string().min(1, "transactions[].description je obavezan"),
        payer: z.string().nullable().optional(),
        payer_account: z.string().nullable().optional(),
        recipient: z.string().nullable().optional(),
        recipient_account: z.string().nullable().optional(),
        reference: z.string().nullable().optional(),
        purpose_code: z.string().nullable().optional(),
        amount: z.number({ required_error: "transactions[].amount je obavezan" }),
        direction: z.enum(["credit", "debit"]),
        balance_after: z.number().nullable().optional(),
      }),
    )
    .min(1, "transactions ne sme biti prazan niz"),
  metadata: z
    .object({
      filename: z.string().nullable().optional(),
      received_at: z.string().nullable().optional(),
      parser: z.string().nullable().optional(),
      parser_version: z.string().nullable().optional(),
    })
    .optional(),
});
export type BankStatementImportInput = z.infer<typeof bankStatementImportSchema>;

export const minimaxSendDailyReportSchema = z.object({
  // YYYY-MM-DD; ako izostane, šalje se izveštaj za juče (vidi MinimaxStockReportService).
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum mora biti u formatu GGGG-MM-DD")
    .optional(),
  // KASA = računi sa naše kase, OTHER = web prodaja / panel (podrazumevano OTHER).
  scope: z.enum(["KASA", "OTHER"]).optional(),
});
export type MinimaxSendDailyReportInput = z.infer<typeof minimaxSendDailyReportSchema>;

export const scanMobileReceiptSchema = z.object({
  url: z
    .string({ required_error: "url je obavezan" })
    .min(1, "url je obavezan")
    .url("url mora biti validan HTTP/HTTPS URL"),
});
export type ScanMobileReceiptInput = z.infer<typeof scanMobileReceiptSchema>;

export const statementProcessingSuccessSchema = z.object({
  job_id: z.string().min(1, "job_id je obavezan"),
  pib: z.string().min(1, "pib je obavezan"),
  import_address: z.string().nullable().optional(),
  filename: z.string().nullable().optional(),
  format: z.string().nullable().optional(),
  bank: z.string().nullable().optional(),
  account_number: z.string().nullable().optional(),
  // Broj izvoda (23.09.2026 - "uskladi ... da se ovo ne desava" / dedup
  // izvoda) - zajedno sa pib + account_number čini "prirodni ključ" izvoda,
  // koristi se da se isti izvod ne pošalje dvaput na MiniMax čak i kad
  // stigne kroz nov mejl (nov job_id).
  statement_number: z.string().nullable().optional(),
  transactions: z.number().nullable().optional(),
  source: z.string().nullable().optional(),
  sender: z.string().nullable().optional(),
  target_email: z.string().nullable().optional(),
});
export type StatementProcessingSuccessInput = z.infer<typeof statementProcessingSuccessSchema>;

export const statementProcessingErrorSchema = z.object({
  job_id: z.string().min(1, "job_id je obavezan"),
  pib: z.string().min(1, "pib je obavezan"),
  import_address: z.string().nullable().optional(),
  error_code: z.string().min(1, "error_code je obavezan"),
  title: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  debug_message: z.string().nullable().optional(),
  filename: z.string().nullable().optional(),
  sender: z.string().nullable().optional(),
  bank: z.string().nullable().optional(),
});
export type StatementProcessingErrorInput = z.infer<typeof statementProcessingErrorSchema>;

// ---------------------------------------------------------------------------
// Print Agent - vidi "Finora - Print Agent API (checkin, licenca, lista
// agenata)" specifikaciju, tačka 4.2. Ograničenja (dužine, regex, max broj
// elemenata) su prepisana TAČNO iz spec-a - ne "razumna" nego DOGOVORENA sa
// agentom (menjati samo ako se spec promeni).
// ---------------------------------------------------------------------------

const printAgentShortString = z.string().max(255, "Najviše 255 znakova");

const printAgentAddressSchema = z.object({
  ip: printAgentShortString,
  interface: printAgentShortString,
  netmask: printAgentShortString.optional(),
});

const printAgentUrlSchema = printAgentShortString.refine(
  (v) => v.startsWith("http://"),
  { message: "url mora počinjati sa http://" },
);

const printAgentPrinterSchema = z.object({
  name: printAgentShortString,
  default: z.boolean().optional(),
});

export const printAgentCheckinSchema = z.object({
  // Ono što je korisnik uneo u agentu - može biti prazno, NIKAD se ne
  // koristi kao izvor firme (ta dolazi iz ključa) - vidi spec 4.3 korak 3,
  // koristi se samo da se otkrije neusklađenost (pogrešan ID firme unet u
  // agentu).
  company_id: z.string().max(255).optional().default(""),
  agent_id: z
    .string()
    .regex(/^[0-9a-f]{24}$/, "agent_id mora biti 24 hex znaka"),
  agent_version: printAgentShortString,
  hostname: printAgentShortString,
  module: z.literal("print_agent"),
  auth_token: z
    .string()
    .regex(/^[0-9a-f]{64}$/, "auth_token mora biti 64 hex znaka"),
  port: z.number().int().min(1).max(65535),
  lan_enabled: z.boolean(),
  addresses: z.array(printAgentAddressSchema).max(16, "Najviše 16 adresa"),
  urls: z.array(printAgentUrlSchema).max(16, "Najviše 16 URL-ova"),
  mdns_host: printAgentShortString.optional(),
  printers: z.array(printAgentPrinterSchema).max(100, "Najviše 100 štampača"),
  default_printer: printAgentShortString.optional().default(""),
  default_thermal_printer: printAgentShortString.optional().default(""),
});
export type PrintAgentCheckinInput = z.infer<typeof printAgentCheckinSchema>;

export const createPrintAgentKeySchema = z.object({
  label: z.string().max(255).optional(),
});
export type CreatePrintAgentKeyInput = z.infer<typeof createPrintAgentKeySchema>;

export const renamePrintAgentSchema = z.object({
  name: z.string().min(1, "Naziv je obavezan").max(255),
});
export type RenamePrintAgentInput = z.infer<typeof renamePrintAgentSchema>;

// --- Automatska stampa (03.10.2026), vidi
// docs/timovi/print-agent-automatska-stampa.md ---

export const assignPrintAgentLocationSchema = z.object({
  businessLocationId: z.string().uuid().nullable(),
});
export type AssignPrintAgentLocationInput = z.infer<typeof assignPrintAgentLocationSchema>;

export const updateBusinessLocationAutoPrintSchema = z.object({
  enabled: z.boolean(),
  printerRole: z.enum(["thermal", "a4"]).optional().default("thermal"),
  copies: z.number().int().min(1).max(20).optional().default(1),
  // Sirina trake kad je printerRole="thermal" (04.10.2026) - nebitno za "a4".
  thermalWidth: z.enum(["80mm", "58mm"]).optional().default("80mm"),
});
export type UpdateBusinessLocationAutoPrintInput = z.infer<typeof updateBusinessLocationAutoPrintSchema>;

// GET /api/print-agent/queue - isti guard kao checkin (agent_id + auth_token),
// ne korisnicki JWT.
export const printAgentQueueAckSchema = z.object({
  status: z.enum(["printed", "failed"]),
  error: z.string().max(500).optional(),
});
export type PrintAgentQueueAckInput = z.infer<typeof printAgentQueueAckSchema>;

// --- Modul "Ugovor" (dogovoreno 23.09.2026, vidi ugovor-modul-todo.md) ----

export const contractPlaceholderDefSchema = z.object({
  key: z.string().min(1),
  type: z.enum(["text", "boolean"]),
  label: z.string().min(1),
});

export const createContractTemplateSchema = z.object({
  name: z.string().min(1, "Naziv šablona je obavezan").max(255),
  bodyText: z.string().min(1, "Tekst ugovora je obavezan"),
  placeholders: z.array(contractPlaceholderDefSchema).default([]),
});
export type CreateContractTemplateInput = z.infer<typeof createContractTemplateSchema>;

export const updateContractTemplateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  bodyText: z.string().min(1).optional(),
  placeholders: z.array(contractPlaceholderDefSchema).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateContractTemplateInput = z.infer<typeof updateContractTemplateSchema>;

export const updateContractSettingsSchema = z.object({
  representativeName: z.string().max(255).nullable().optional(),
  esirApprovalDate: z.string().datetime().nullable().optional(),
  signingPlace: z.string().max(255).nullable().optional(),
  customFields: z.record(z.string()).nullable().optional(),
});
export type UpdateContractSettingsInput = z.infer<typeof updateContractSettingsSchema>;

// Globalna podesavanja modula "Ugovor" (01.10.2026) - zamenjuje
// ContractSettings PO FIRMI (iznad, ostavljeno zbog istorijskih ugovora).
// Vidi contract-placeholder.util.ts.
export const updateContractProviderSettingsSchema = z.object({
  representativeName: z.string().max(255).nullable().optional(),
  esirApprovalNumber: z.string().max(255).nullable().optional(),
  esirApprovalDate: z.string().datetime().nullable().optional(),
  paymentDueDays: z.number().int().min(1).max(365).optional(),
  suspensionDays: z.number().int().min(1).max(365).optional(),
  courtCity: z.string().min(1).max(255).optional(),
  slaPremiumPrice: z.number().min(0).optional(),
  contractDiscount12Percent: z.number().min(0).max(100).optional(),
  contractDiscount24Percent: z.number().min(0).max(100).optional(),
});
export type UpdateContractProviderSettingsInput = z.infer<typeof updateContractProviderSettingsSchema>;

// Admin "Generiši ugovor" dugme (01.10.2026) - opciona cena Premium podrške
// SAMO za ovog klijenta (vidi razgovor "cena supporta po korisniku").
// null = ukloni override (vrati se na globalnu cenu), undefined/izostavljeno
// = ne diraj postojeću vrednost.
export const generateContractSchema = z.object({
  slaPremiumPriceOverride: z.number().min(0).nullable().optional(),
  // Rucni popust u % (null = podrazumevani po periodu)
  discountPercentOverride: z.number().min(0).max(100).nullable().optional(),
  // Period ugovora (05.10.2026); izostavljeno = ne diraj / za novi ugovor neodredjeno.
  durationOption: z.enum(["UNDEFINED", "MONTHS_12", "MONTHS_24"]).optional(),
});
export type GenerateContractInput = z.infer<typeof generateContractSchema>;

// Prihvatanje ugovora na kraju onboardinga (dogovoreno 23.09.2026, "Slažem
// se sa ugovorom i potpisaću ga" + broj lične karte/mesto izdavanja).
export const acceptContractSchema = z.object({
  templateId: z.string().uuid(),
  durationOption: z.enum(["UNDEFINED", "MONTHS_12", "MONTHS_24"]),
  slaLevel: z.enum(["STANDARD", "PREMIUM"]),
  signatoryIdCardNumber: z.string().min(1, "Broj lične karte je obavezan").max(64),
  signatoryIdCardIssuePlace: z.string().min(1, "Mesto izdavanja je obavezno").max(255),
});
export type AcceptContractInput = z.infer<typeof acceptContractSchema>;

export const reviewContractSchema = z.object({
  decision: z.enum(["APPROVED", "REJECTED"]),
  rejectionReason: z.string().max(1000).optional(),
});
export type ReviewContractInput = z.infer<typeof reviewContractSchema>;

// --- Modul "Troškovi" (dogovoreno 27.09.2026) -------------------------------

// Stavka troška (dogovoreno 30.09.2026 - "stavka kolicina cena ukupno... da
// se vidi koliki je pdv") - isti oblik/validacija kao
// addSalesDocumentItemSchema (bez popusta - trošak nema koncept popusta).
//
// Poreska stopa stavke - TRI načina (dopunjeno 01.10.2026, vidi
// docs/timovi/troskovi-pdv-sa-racuna.md - "ne moze nas sistem da odredjuje
// koji ce PDV biti. fiskalni racun je verodostojan a ne nasa kasa"):
//  - taxRateId: ručni unos (Web UI) - korisnik bira SVOJU postojeću stopu.
//  - taxRatePercent: procenat SA RAČUNA (npr. "10") - server ga koristi
//    direktno kao poresku stopu stavke, BEZ zahteva da firma ima baš tu
//    stopu definisanu (za skenirani račun gde dobavljač koristi stopu koju
//    firma nema u svojim poreskim stopama).
//  - taxAmount: iznos PDV-a kako je odštampan na računu (umesto procenta) -
//    još verniji način, ne oslanja se na zaokruživanje; server iz njega
//    izvodi osnovicu (ExpensesService.prepareItems).
// Mora biti prosleđeno TAČNO JEDNO od ova tri polja.
export const expenseItemInputSchema = z
  .object({
    name: z.string().min(1, "Naziv stavke je obavezan").max(255),
    quantity: quantitySchema,
    unitPriceGross: moneyAmountSchema,
    taxRateId: z.string().uuid().optional(),
    taxRatePercent: z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, 'taxRatePercent mora biti procenat, npr. "10"')
      .optional(),
    taxAmount: nonNegativeMoneyAmountSchema.optional(),
  })
  .refine(
    (item) => [item.taxRateId, item.taxRatePercent, item.taxAmount].filter((v) => v !== undefined).length === 1,
    {
      message: "Stavka mora imati tačno jedno od: taxRateId, taxRatePercent, taxAmount",
      path: ["taxRateId"],
    },
  );
export type ExpenseItemInput = z.infer<typeof expenseItemInputSchema>;

export const createExpenseSchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum mora biti u formatu GGGG-MM-DD"),
  supplierName: z.string().max(255).nullable().optional(),
  supplierPib: z.string().max(32).nullable().optional(),
  items: z.array(expenseItemInputSchema).min(1, "Trošak mora imati bar jednu stavku"),
  description: z.string().max(2000).nullable().optional(),
  attachmentUrl: z.string().max(2000).nullable().optional(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

export const updateExpenseSchema = createExpenseSchema.partial();
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

// Napomena: `.refine()` ispod validira samo polja prisutna u OVOM pozivu
// (npr. PATCH sa samo {enabled: true} ne nosi accountantEmail) - servisni
// sloj (ExpensesService.updateSettings) MORA dodatno proveriti KONAČNO,
// spojeno stanje (destination + accountantEmail) pre upisa, jer parcijalni
// pozivi ovde prolaze i kad konačno stanje ne bi bilo validno.
export const updateExpenseSettingsSchema = z
  .object({
    enabled: z.boolean().optional(),
    destination: z.enum(["ACCOUNTANT_EMAIL", "MINIMAX"]).optional(),
    accountantEmail: z
      .string()
      .trim()
      .toLowerCase()
      .email("Email adresa nije ispravna")
      .max(255)
      .optional()
      .or(z.literal("")),
  })
  .refine(
    (data) => data.destination !== "ACCOUNTANT_EMAIL" || !!data.accountantEmail,
    {
      message: "Email knjigovođe je obavezan kad je odredište \"Knjigovođa (email)\"",
      path: ["accountantEmail"],
    },
  );
export type UpdateExpenseSettingsInput = z.infer<typeof updateExpenseSettingsSchema>;

// Mobile QR skener (Finora Mobile) već postoji - ovo je NOVI korak "sačuvaj
// skenirani račun kao trošak", posle postojećeg POST /mobile/receipts/scan.
// Ažurirano 30.09.2026 - `amount` je zamenjeno sa `items[]` (isto kao Web UI
// unos), da bi i mobilno sačuvani trošak imao stavke/PDV (vidi
// claude/troskovi-modul-mobilni-kontrakt.md).
export const saveMobileReceiptExpenseSchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum mora biti u formatu GGGG-MM-DD"),
  supplierName: z.string().max(255).nullable().optional(),
  supplierPib: z.string().max(32).nullable().optional(),
  items: z.array(expenseItemInputSchema).min(1, "Trošak mora imati bar jednu stavku"),
  fiscalNumber: z.string().max(64).nullable().optional(),
  verificationUrl: z.string().max(2000).nullable().optional(),
});
export type SaveMobileReceiptExpenseInput = z.infer<typeof saveMobileReceiptExpenseSchema>;

// --- Podrška / tiketi (30.09.2026) ------------------------------------------
export const createSupportTicketSchema = z.object({
  subject: z.string().trim().min(3, "Naslov mora imati bar 3 karaktera").max(200),
  message: z.string().trim().min(1, "Poruka ne sme biti prazna").max(10000),
  priority: z.enum(["LOW", "MEDIUM", "HIGH"]).optional(),
  category: z.string().trim().max(100).nullable().optional(),
});
export type CreateSupportTicketInput = z.infer<typeof createSupportTicketSchema>;

export const addSupportTicketMessageSchema = z.object({
  message: z.string().trim().min(1, "Poruka ne sme biti prazna").max(10000),
});
export type AddSupportTicketMessageInput = z.infer<typeof addSupportTicketMessageSchema>;

export const updateSupportTicketStatusSchema = z.object({
  status: z.enum(["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"]),
});
export type UpdateSupportTicketStatusInput = z.infer<typeof updateSupportTicketStatusSchema>;
